/* Quick add from product cards, hotspots and the bag upsell, plus the cards' desktop hover photo. Uses Shopify's
   standard Ajax Cart endpoint (/cart/add.js) through LMB.cart (assets/cart.js), so Shopify's product_added_to_cart
   customer event fires for every pixel (verified: GA4 add_to_cart, Meta AddToCart).
   - [data-quick-add="<variant id>"]: adds one of that variant. Outside the bag the bag stays closed: the button
     shows a tick and the toast (assets/toast.js) says what's left to free shipping. Inside the bag (upsell) the
     bag re-renders as before.
   - [data-quick-add-choose]: opens the size sheet (assets/size-sheet.js) for a card's piece with real variants;
     its "Add to bag" is a [data-quick-add] button like any other, so every add has exactly one handler.
   */
(() => {
  if (window.LMBQuickAdd) return;
  window.LMBQuickAdd = true;

  const LMB = (window.LMB = window.LMB || {});
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const t = (key, fallback) => (LMB.strings && LMB.strings[key]) || fallback;
  const ADDED_MS = 1800;

  const announce = (el, message) => {
    const scope = el.closest('[data-product-card], [data-quick-add-scope]');
    const status = scope && scope.querySelector('[data-quick-add-status]');
    if (status) status.textContent = message;
  };

  async function add(button) {
    const id = Number(button.dataset.quickAdd);
    if (!id || button.getAttribute('aria-busy') === 'true') return;
    // From the size sheet, the card's "+" that opened it is where the tick shows and focus returns.
    const sheet = button.closest('[data-size-sheet]');
    const toggle = sheet && LMB.sizeSheet ? LMB.sizeSheet.trigger() : null;
    const done = toggle || button;
    const inBag = !!button.closest('cart-drawer, lmb-cart-page');
    // Inside the open bag (upsell), keep the bag's own opener as the place focus returns to.
    const inDrawer = !!button.closest('cart-drawer');
    const payload = { items: [{ id, quantity: 1 }] };
    button.setAttribute('aria-busy', 'true');
    try {
      if (LMB.cart) {
        // assets/cart.js: /cart/add.js with the bag rendered in the same request.
        if (inBag) {
          await LMB.cart.add(payload, { trigger: inDrawer ? undefined : button, source: 'bag' });
        } else {
          await LMB.cart.add(payload, { open: false, trigger: done, source: 'card' });
        }
      } else {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || t('quick_add_error', 'Could not add this piece.'));
        const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
        if (LMB.updateCartCount) LMB.updateCartCount(cart.item_count);
      }
      if (sheet && LMB.sizeSheet) LMB.sizeSheet.close();
      // Card adds are announced by the toast (role=status); without it, by the card's own status line.
      if (inBag || !LMB.toast || !LMB.cart) announce(done, t('quick_add_added', 'Added to bag'));
      done.classList.add('is-added');
      setTimeout(() => done.classList.remove('is-added'), ADDED_MS);
    } catch (error) {
      // Read out here (in the sheet: its own status line); the toast shows the same text for card adds.
      announce(button, error.message);
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 2500);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-quick-add]');
    if (addButton) {
      event.preventDefault();
      add(addButton);
      return;
    }
    const chooser = event.target.closest('[data-quick-add-choose]');
    if (chooser) {
      event.preventDefault();
      const card = chooser.closest('[data-product-card]');
      // Only one overlay is open at a time, so a card inside the bag can't open the sheet over it.
      const inBag = !!chooser.closest('cart-drawer');
      if (!inBag && LMB.sizeSheet && card && card.querySelector('[data-card-sheet]')) {
        LMB.sizeSheet.open(card, chooser);
      } else if (chooser.dataset.productUrl) {
        // No sheet here (or its script didn't load): the product page has the same size choice.
        window.location.href = chooser.dataset.productUrl;
      }
    }
  });

  /* Desktop hover photo: the card lists its second photo's files (data-hover-*); the image is made on the first
     mouse hover only, so touch screens and cards nobody points at never download it. */
  const hoverQuery = window.matchMedia('(hover: hover) and (min-width: 990px)');
  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'mouse' || !hoverQuery.matches) return;
      const wrap = event.target.closest && event.target.closest('[data-hover-srcset]');
      if (!wrap) return;
      const media = wrap.querySelector('.card__media');
      const srcset = wrap.dataset.hoverSrcset;
      wrap.removeAttribute('data-hover-srcset');
      if (!media) return;
      const img = document.createElement('img');
      img.className = 'card__hover';
      img.alt = '';
      img.decoding = 'async';
      img.sizes = wrap.dataset.hoverSizes || '';
      img.srcset = srcset;
      img.src = wrap.dataset.hoverSrc;
      img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
      const first = media.querySelector('img');
      if (first) first.after(img);
      else media.prepend(img);
    },
    { passive: true }
  );
})();
